import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha, styled } from "@mui/material/styles";

import { MASCOT_PALETTE } from "@/components/Others/MascotPanel";
import { colors } from "@/theme";

import { OddDirection } from "../../types";
import {
  accentColor,
  borderColor,
  flickerAnimationSx,
  headerBg,
  isDarkTheme,
} from "../../utils/matchOdds";
import { headerFocusRing } from "./shared";

/* Price buttons: a list row's cell and a market card's selection, with the flick on a change. */

/**
 * One price, as the button it is.
 *
 * The price is the brightest thing in the cell and the line beside it
 * ("+8.5", "O 160.5") is muted: the number is what a player compares along
 * the row. A price in the slip stands in white on the brand gradient, the
 * way the artwork draws its own odds; a suspended one is dim and does
 * nothing. The cell sets the ink the price animates back to, so a flick on a
 * change ends on white in a chosen cell and on the page ink elsewhere.
 */
export const OddsCellRoot = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "locked",
})<{ active: boolean; locked: boolean }>(({ theme, active, locked }) => {
  const dark = isDarkTheme(theme);
  const lit = dark ? MASCOT_PALETTE.sky : accentColor(theme);
  const tint = alpha(colors.prime, dark ? 0.14 : 0.1);

  return {
    "--odds-ink": theme.palette.text.primary,
    width: "100%",
    minHeight: 40,
    padding: "6px 8px",
    // A thumb needs 44 px; a mouse keeps the denser row.
    "@media (pointer: coarse)": { minHeight: 44 },
    gap: 4,
    borderRadius: "8px",
    fontFamily: "inherit",
    background: headerBg(theme),
    border: `1px solid ${borderColor(theme)}`,
    color: "var(--odds-ink)",
    cursor: locked ? "default" : "pointer",
    transition:
      "border-color .2s ease, background-color .2s ease, box-shadow .2s ease, transform .1s ease",
    "&:focus-visible": { outline: `2px solid ${lit}`, outlineOffset: 2 },
    ...(locked && { "--odds-ink": theme.palette.text.disabled }),
    ...(!locked &&
      !active && {
        "@media (hover: hover)": {
          "&:hover": { borderColor: lit, background: tint },
        },
        "&:active": { transform: "scale(0.97)" },
      }),
    ...(active && {
      "--odds-ink": colors.white,
      background: colors.gradient,
      borderColor: "transparent",
      boxShadow: `0 4px 14px ${alpha(colors.prime, 0.35)}`,
      "@media (hover: hover)": {
        "&:hover": { filter: "brightness(1.08)" },
      },
      "&:active": { transform: "scale(0.97)" },
    }),
    // Last, so it wins over the press scale above.
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "&:active": { transform: "none" },
    },
  };
});

export const DirectionArrow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "direction",
})<{ direction: OddDirection }>(({ theme, direction }) => ({
  position: "absolute",
  left: "100%",
  top: "50%",
  transform: "translateY(-50%)",
  marginLeft: 2,
  display: "inline-flex",
  color: direction === "up" ? accentColor(theme) : colors.red,
}));

/** The line beside a price ("+8.5", "O 160.5"): the cell's ink, dimmed, so the price stands in front. */
export const OutcomeLabel = styled(Typography)({
  minWidth: 0,
  fontSize: 11,
  lineHeight: 1.1,
  fontWeight: 600,
  letterSpacing: -0.1,
  color: "inherit",
  opacity: 0.72,
});

export const Price = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "direction" && prop !== "size",
})<{ direction?: OddDirection | null; size?: "sm" | "md" }>(
  ({ theme, direction, size = "md" }) => ({
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    color: `var(--odds-ink, ${theme.palette.text.primary})`,
    ...(size === "sm" && { fontSize: 13, lineHeight: 1.1 }),
    ...(direction && flickerAnimationSx(theme, direction)),
    // Reduced motion: hold the move's colour for the same time instead of flashing; the end event still clears it.
    ...(direction && {
      "@keyframes oddHold-up": { "0%, 100%": { color: accentColor(theme) } },
      "@keyframes oddHold-down": { "0%, 100%": { color: colors.red } },
      "@media (prefers-reduced-motion: reduce)": {
        animationName: `oddHold-${direction}`,
        animationTimingFunction: "linear",
      },
    }),
  }),
);

export const MarketCardHeader = styled(Stack)(({ theme }) => ({
  paddingLeft: 16,
  paddingRight: 16,
  paddingTop: 10,
  paddingBottom: 10,
  cursor: "pointer",
  userSelect: "none",
  background: headerBg(theme),
  transition: "background-color .15s ease",
  "@media (hover: hover)": {
    "&:hover": { backgroundColor: alpha(colors.prime, isDarkTheme(theme) ? 0.1 : 0.06) },
  },
  ...headerFocusRing(theme),
}));

export const SelectionsGrid = styled(Box, {
  shouldForwardProp: (prop) => prop !== "columns" && prop !== "phoneColumns",
})<{ columns: number; phoneColumns?: number }>(({ columns, phoneColumns }) => ({
  display: "grid",
  gridTemplateColumns: `repeat(${phoneColumns ?? (columns <= 3 ? columns : 2)}, 1fr)`,
  gap: 8,
  padding: 16,
  "@media (min-width: 900px)": {
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
  },
}));

/** A price on the match page's market cards: the list's cell at a market card's size. */
export const OddItemRoot = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "locked",
})<{ active: boolean; locked: boolean }>(({ theme, active, locked }) => {
  const dark = isDarkTheme(theme);
  const lit = dark ? MASCOT_PALETTE.sky : accentColor(theme);
  const tint = alpha(colors.prime, dark ? 0.14 : 0.1);

  return {
    "--odds-ink": theme.palette.text.primary,
    width: "100%",
    minWidth: 0,
    minHeight: 40,
    "@media (pointer: coarse)": { minHeight: 44 },
    justifyContent: "space-between",
    gap: 8,
    paddingLeft: 12,
    paddingRight: 12,
    paddingTop: 8,
    paddingBottom: 8,
    borderRadius: "8px",
    fontFamily: "inherit",
    textAlign: "left",
    background: headerBg(theme),
    border: `1px solid ${borderColor(theme)}`,
    color: "var(--odds-ink)",
    cursor: locked ? "default" : "pointer",
    transition:
      "border-color .2s ease, background-color .2s ease, box-shadow .2s ease, transform .1s ease",
    "&:focus-visible": { outline: `2px solid ${lit}`, outlineOffset: 2 },
    ...(locked && { "--odds-ink": theme.palette.text.disabled }),
    ...(!locked &&
      !active && {
        "@media (hover: hover)": {
          "&:hover": { borderColor: lit, background: tint },
        },
        "&:active": { transform: "scale(0.97)" },
      }),
    ...(active && {
      "--odds-ink": colors.white,
      background: colors.gradient,
      borderColor: "transparent",
      boxShadow: `0 4px 14px ${alpha(colors.prime, 0.35)}`,
      "@media (hover: hover)": {
        "&:hover": { filter: "brightness(1.08)" },
      },
      "&:active": { transform: "scale(0.97)" },
    }),
    // Last, so it wins over the press scale above.
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "&:active": { transform: "none" },
    },
  };
});

/**
 * The name of a selection on a market card: two lines at most, so a phone
 * shows "Saitama Seibu Lions -2.5" whole rather than "Saitama Sei…" with the
 * line, the part that tells two rows apart, cut off.
 */
export const OddLabel = styled(Typography)({
  minWidth: 0,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  lineHeight: 1.25,
  textAlign: "left",
});
